import React from 'react';
import turfDistance from '@turf/distance';
import { pointItemToLngLat } from './mapbox/sources/point_items';
import { LanguageSupportUtil } from './language_support_util';

const SearchResultsUtil = {
  createWeightedResultList(results, center, weightDistance) {
    const scale = weightDistance;
    const decay = 0.5;
    const magicSortFactor = -2 * (Math.pow(scale, 2) / (2 * Math.log(decay)));
    const centerLngLat = [center[1], center[0]];
    return results
      .map(result => {
        const distanceToCenter = turfDistance(centerLngLat, pointItemToLngLat(result)) * 1000;
        result.adjustedPopularity = result.popularity * Math.exp((-1 * Math.pow(distanceToCenter, 2)) / magicSortFactor);
        return result;
      })
      .sort((a, b) => b.adjustedPopularity - a.adjustedPopularity);
  },
  createPopularResultList(results) {
    return results.sort((a, b) => b.popularity - a.popularity);
  },
  createClosestResultList(results, center) {
    const centerLngLat = [center[1], center[0]];
    return results
      .map(result => {
        result.distanceToCenter = turfDistance(centerLngLat, pointItemToLngLat(result));
        return result;
      })
      .sort((a, b) => a.distanceToCenter - b.distanceToCenter);
  },
  sortByCustomListOrder(results, filters, getListItem) {
    if (!getListItem) {
      return results;
    }
    // favorite, completed, and custom list pages
    return results.sort((a, b) => {
      a = getListItem(a.type, a.ID);
      b = getListItem(b.type, b.ID);
      // descending order
      if (filters.sort['list-order'].direction === -1) {
        return (b ? b.order : null) - (a ? a.order : null);
      }
      // ascendening order
      return (a ? a.order : null) - (b ? b.order : null);
    });
  },
  sortByName(results, filters) {
    return results.sort((a, b) => {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();
      if (filters.sort.name.direction === 1) {
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      }

      if (nameA < nameB) return 1;
      if (nameA > nameB) return -1;
      return 0;
    });
  },
  sortByDate(results, filters) {
    return results.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      if (filters.sort.date.direction === -1) return dateA - dateB;
      return dateB - dateA;
    });
  },
  applySortFunction(items, filters, center, weightDistance, getListItem = null) {
    const selectedFilterKey = Object.keys(filters.sort).filter(itemKey => {
      return filters.sort[itemKey].selected;
    })[0];
    if (selectedFilterKey) {
      if (selectedFilterKey == 'best-match') return this.createWeightedResultList(items, center, weightDistance);
      if (selectedFilterKey == 'most-popular') return this.createPopularResultList(items);
      if (selectedFilterKey == 'closest') return this.createClosestResultList(items, center);
      if (selectedFilterKey == 'list-order') return this.sortByCustomListOrder(items, filters, getListItem);
      if (selectedFilterKey == 'name') return this.sortByName(items, filters);
      if (selectedFilterKey == 'date') return this.sortByDate(items, filters);
    }
    return items;
  },
  formatTrailInfo(hit, languageRegionCode = 'en-US') {
    let resultString = '';

    if (hit.area_name && hit.area_name != '') {
      resultString += LanguageSupportUtil.findRecordFieldWithLocale(hit, 'area_name', languageRegionCode);
    }

    if (hit.city_name && hit.city_name != '') {
      resultString += (resultString == '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'city_name', languageRegionCode);
    }

    if (hit.state_name && hit.state_name != '') {
      resultString += (resultString == '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'state_name', languageRegionCode);
    }

    if (hit.country_name && hit.country_name != '' && hit.country_id != 313) {
      resultString += (resultString == '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'country_name', languageRegionCode);
    }

    return resultString;
  },
  formatNonTrailInfo(hit, languageRegionCode = 'en-US') {
    let resultString = '';
    if (hit.type !== 'area' || (hit.area_type && hit.area_type === 'C')) {
      if (hit.city_name !== undefined && hit.city_name != '' && hit.city_name != null) {
        resultString += (resultString === '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'city_name', languageRegionCode);
      }
    }
    if (hit.state_name !== undefined && hit.state_name !== '' && hit.state_name != null) {
      resultString += (resultString == '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'state_name', languageRegionCode);
    }
    if (hit.country_name !== '' && hit.country_name != undefined && (resultString === '' || hit.country_name !== 'United States of America')) {
      resultString += (resultString == '' ? '' : ', ') + LanguageSupportUtil.findRecordFieldWithLocale(hit, 'country_name', languageRegionCode);
    }

    return resultString;
  },
  formatLocationInfo(hit, languageRegionCode = 'en-US') {
    return hit.type === 'trail' ? this.formatTrailInfo(hit, languageRegionCode) : this.formatNonTrailInfo(hit, languageRegionCode);
  },
  createResultUrl(hit, languageRegionCode = 'en-US') {
    switch (hit.type) {
      case 'track':
        return LanguageSupportUtil.wrapUrlSafe(`/explore/recording/${hit.slug}`, languageRegionCode);
      case 'map':
        return LanguageSupportUtil.wrapUrlSafe(`/explore/map/${hit.slug}`, languageRegionCode);
      case 'list':
        return LanguageSupportUtil.wrapUrlSafe(`/lists/${hit.slug}`, languageRegionCode);
      default:
        return LanguageSupportUtil.wrapUrlSafe(`/${hit.slug}`, languageRegionCode);
    }
  },

  /**
   * formatSearchResult formats an Algolia record as needed for our search results
   * @param {object} hit
   * @param {string} languageRegionCode
   * @returns object
   */
  formatSearchResult(hit, languageRegionCode = 'en-US') {
    const name = LanguageSupportUtil.findRecordFieldWithLocale(hit, 'name', languageRegionCode);
    let highlightedName = name;
    if (hit._highlightResult) {
      highlightedName = LanguageSupportUtil.findRecordFieldWithLocale(hit._highlightResult, 'name', languageRegionCode).value;
    }

    return {
      location: this.formatLocationInfo(hit, languageRegionCode),
      url: this.createResultUrl(hit, languageRegionCode),
      name,
      highlightedName,
      type: hit.type,
      objectID: hit.objectID
    };
  }
};

export { SearchResultsUtil };
